/**
 * /pricing — Phase 2 public catalog page.
 *
 * Live-queried from `/api/public/peptide-pricing`. Groups peptides by category
 * and shows a comparison table per group. GLP-1 program is featured at the top.
 *
 * IMPORTANT: peptides missing `vials_per_month` in the catalog are intentionally
 * skipped server-side — never default to 1.0 (would undercharge low-frequency
 * peptides). The API returns only priceable rows.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, ShieldCheck, Truck, Stethoscope, Pill, Sparkles, Brain, Heart, Dumbbell, Flame, Zap, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Small icon picker so each group has a visual cue.
const CATEGORY_ICONS = {
  "Weight Loss": Flame,
  "Weight Management": Flame,
  "Recovery": Zap,
  "Recovery & Healing": Zap,
  "Recovery & Performance": Dumbbell,
  "Anti-Aging": Sparkles,
  "Anti-Aging & Skin": Sparkles,
  "Cognitive": Brain,
  "Cognitive Enhancement": Brain,
  "Cognitive Health": Brain,
  "Sexual Health": Heart,
  "Sexual Wellness": Heart,
  "Longevity": Sparkles,
  "Immune Defense": ShieldCheck,
  "Growth Hormone": Dumbbell,
  "General": Pill,
};

export default function PricingPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/public/peptide-pricing`)
      .then((r) => {
        if (!cancelled) {
          setData(r.data);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e.response?.data?.detail || "Couldn't load pricing.");
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div data-testid="pricing-loading" className="min-h-screen flex items-center justify-center bg-[#F7F5F0]">
        <Loader2 size={28} className="animate-spin text-[#5C715E]" />
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="pricing-error" className="min-h-screen flex items-center justify-center bg-[#F7F5F0]">
        <p className="text-sm font-sans text-red-700">{error}</p>
      </div>
    );
  }

  // Split GLP-1 from the rest (GLP-1 gets the spotlight section).
  const glp1Peptides = (data.peptides || []).filter((p) => p.is_glp1);
  const otherPeptides = (data.peptides || []).filter((p) => !p.is_glp1);

  // Group non-GLP-1 by category.
  const groups = {};
  for (const p of otherPeptides) {
    const cat = p.category || "General";
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(p);
  }
  const groupNames = Object.keys(groups).sort();

  return (
    <div data-testid="pricing-page" className="min-h-screen bg-[#F7F5F0]">
      {/* Hero */}
      <section className="pt-28 pb-12 md:pt-36 md:pb-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <span className="text-xs md:text-sm font-sans font-medium uppercase tracking-[0.2em] text-[#C47C5D] mb-4 block">
            Transparent Pricing
          </span>
          <h1 data-testid="pricing-page-heading" className="text-4xl md:text-5xl lg:text-6xl font-serif font-light tracking-tighter text-[#2A3324] mb-5">
            One monthly price. Everything included.
          </h1>
          <p className="text-base md:text-lg font-sans font-light text-[#2A3324]/70 max-w-2xl mx-auto">
            {data.all_inclusive_blurb}
          </p>

          <div className="mt-8 flex items-center justify-center gap-4 flex-wrap text-xs md:text-sm font-sans text-[#2A3324]/70">
            <span className="inline-flex items-center gap-2 bg-white px-3 py-2 rounded-full border border-[#2A3324]/10">
              <Stethoscope size={14} className="text-[#5C715E]" /> Doctor consult included
            </span>
            <span className="inline-flex items-center gap-2 bg-white px-3 py-2 rounded-full border border-[#2A3324]/10">
              <Truck size={14} className="text-[#5C715E]" /> Free overnight shipping
            </span>
            {data.money_back?.enabled && (
              <span className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 px-3 py-2 rounded-full">
                <ShieldCheck size={14} /> 30-day money-back guarantee
              </span>
            )}
          </div>
        </div>
      </section>

      {/* GLP-1 Spotlight */}
      {glp1Peptides.length > 0 && (
        <section className="pb-12">
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <div data-testid="pricing-glp1-spotlight" className="bg-gradient-to-br from-[#5C715E] to-[#4A5A4B] rounded-[2rem] p-8 md:p-12 text-[#F7F5F0]">
              <div className="flex items-start justify-between gap-6 flex-wrap mb-6">
                <div>
                  <span className="text-xs font-sans font-medium uppercase tracking-[0.2em] text-[#F7F5F0]/60 mb-2 block">
                    Most Popular
                  </span>
                  <h2 className="text-3xl md:text-4xl font-serif font-light tracking-tight">GLP-1 Weight Loss Program</h2>
                  <p className="mt-2 font-sans text-[#F7F5F0]/70 max-w-md text-sm">
                    All GLP-1 medications (Semaglutide, Tirzepatide, Retatrutide) ship under one flat program fee — no per-vial math, no surprise charges.
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-5xl md:text-6xl font-serif font-semibold">$400</p>
                  <p className="font-sans text-[#F7F5F0]/60 text-sm">/mo program fee</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
                {glp1Peptides.slice(0, 9).map((p) => (
                  <div key={p.name} data-testid={`pricing-glp1-row-${p.name}`} className="bg-[#F7F5F0]/10 rounded-xl px-3 py-2.5 text-xs font-sans text-[#F7F5F0]">
                    <div className="font-medium">{p.name}</div>
                    {p.recommended_dose && <div className="text-[#F7F5F0]/50 text-[11px] mt-0.5">{p.recommended_dose} · {p.recommended_frequency || "Weekly"}</div>}
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <a href="/#quiz" data-testid="pricing-glp1-cta" className="inline-flex items-center gap-2 bg-[#F7F5F0] text-[#2A3324] hover:bg-white rounded-full px-6 py-3 text-sm font-sans font-medium transition-all">
                  Take the quiz <ArrowRight size={14} />
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Per-category comparison tables */}
      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 space-y-10">
          {groupNames.map((cat) => {
            const Icon = CATEGORY_ICONS[cat] || Pill;
            return (
              <div key={cat} data-testid={`pricing-group-${cat.replace(/\s+/g,'-').toLowerCase()}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#5C715E]/10 rounded-xl flex items-center justify-center">
                    <Icon size={20} strokeWidth={1.5} className="text-[#5C715E]" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-serif font-light tracking-tight text-[#2A3324]">{cat}</h2>
                </div>

                <div className="bg-white rounded-2xl border border-[#2A3324]/5 overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#2A3324]/5">
                        <th className="text-left px-5 py-3 text-[11px] font-sans font-medium uppercase tracking-wider text-[#2A3324]/40">Peptide</th>
                        <th className="text-left px-5 py-3 text-[11px] font-sans font-medium uppercase tracking-wider text-[#2A3324]/40 hidden md:table-cell">What it's for</th>
                        <th className="text-right px-5 py-3 text-[11px] font-sans font-medium uppercase tracking-wider text-[#2A3324]/40">Monthly</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groups[cat].map((p) => (
                        <tr key={p.name} className="border-b last:border-b-0 border-[#2A3324]/5 hover:bg-[#F7F5F0]/40 transition-colors">
                          <td className="px-5 py-4">
                            <div className="font-sans font-medium text-sm text-[#2A3324]">{p.name}</div>
                            {p.recommended_dose && (
                              <div className="text-[11px] font-sans text-[#2A3324]/40 mt-0.5">
                                {p.recommended_dose}{p.recommended_frequency ? ` · ${p.recommended_frequency}` : ""}
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-4 text-xs font-sans text-[#2A3324]/60 hidden md:table-cell max-w-md">
                            {p.short_description || ""}
                          </td>
                          <td className="px-5 py-4 text-right whitespace-nowrap">
                            <div className="font-serif text-lg text-[#2A3324]">${Math.round(p.display_price_monthly)}</div>
                            <div className="text-[10px] font-sans text-[#2A3324]/40">{p.price_label}</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="bg-white rounded-[2rem] border border-[#2A3324]/5 p-10 md:p-14 text-center">
            <h2 className="text-2xl md:text-3xl font-serif font-light tracking-tight text-[#2A3324] mb-4">
              Not sure which protocol is right for you?
            </h2>
            <p className="text-sm md:text-base font-sans text-[#2A3324]/60 max-w-md mx-auto mb-7">
              Our 2-minute quiz matches you to a physician-recommended protocol. Free, no obligation.
            </p>
            <a
              data-testid="pricing-bottom-cta"
              href="/#quiz"
              className="inline-flex items-center gap-2 bg-[#5C715E] hover:bg-[#4A5A4B] text-[#F7F5F0] rounded-full px-8 py-4 text-base font-sans font-medium tracking-wide transition-all shadow-sm hover:-translate-y-1"
            >
              Take the quiz <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
